import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingCart, Plus, Minus, Check } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductPage = ({ addToCart }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
      } catch (e) {
        console.error("Error fetching product:", e);
        navigate("/shop");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, navigate]);

  const handleAddToCart = async () => {
    await addToCart(product.id, quantity);
    setAddedToCart(true);
    toast.success(`${product.name} added to cart`, {
      description: `Quantity: ${quantity}`,
    });
    setTimeout(() => setAddedToCart(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen pt-20" data-testid="product-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Shop
          </Button>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-card border border-border">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
              {product.featured && (
                <Badge className="absolute top-4 left-4 bg-accent text-accent-foreground">
                  Featured
                </Badge>
              )}
              {product.stock <= 5 && product.stock > 0 && (
                <Badge className="absolute top-4 right-4 bg-secondary text-secondary-foreground">
                  Only {product.stock} left
                </Badge>
              )}
            </div>
          </motion.div>

          {/* Product Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col"
          >
            {/* Category */}
            <Link
              to={`/shop/${product.category}`}
              className="inline-block mb-2"
            >
              <Badge
                variant="outline"
                className="border-primary/50 text-primary hover:bg-primary/10"
              >
                {product.category.replace("-", " ").toUpperCase()}
              </Badge>
            </Link>

            {/* Title */}
            <h1
              className="font-unbounded text-3xl md:text-4xl font-bold text-foreground mb-4"
              data-testid="product-name"
            >
              {product.name}
            </h1>

            {/* Price */}
            <div className="mb-6">
              <span
                className="font-unbounded text-4xl font-bold text-primary neon-text"
                data-testid="product-price"
              >
                ${product.price.toFixed(2)}
              </span>
            </div>

            {/* Description */}
            <p
              className="font-outfit text-lg text-muted-foreground mb-8"
              data-testid="product-description"
            >
              {product.description}
            </p>

            <Separator className="mb-8 bg-border" />

            {/* Stock Status */}
            <div className="flex items-center gap-2 mb-6">
              {product.stock > 0 ? (
                <>
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="font-outfit text-sm text-green-500">
                    In Stock ({product.stock} available)
                  </span>
                </>
              ) : (
                <>
                  <div className="w-3 h-3 rounded-full bg-accent" />
                  <span className="font-outfit text-sm text-accent">
                    Out of Stock
                  </span>
                </>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-4 mb-8">
              <span className="font-outfit text-sm text-muted-foreground">
                Quantity:
              </span>
              <div className="flex items-center gap-3">
                <Button
                  data-testid="decrease-quantity"
                  variant="outline"
                  size="icon"
                  className="w-10 h-10 border-border hover:bg-muted"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span
                  className="font-outfit text-lg font-medium w-12 text-center"
                  data-testid="quantity-value"
                >
                  {quantity}
                </span>
                <Button
                  data-testid="increase-quantity"
                  variant="outline"
                  size="icon"
                  className="w-10 h-10 border-border hover:bg-muted"
                  onClick={() =>
                    setQuantity(Math.min(product.stock, quantity + 1))
                  }
                  disabled={quantity >= product.stock}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Add to Cart */}
            <Button
              data-testid="add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={product.stock === 0 || addedToCart}
              className={`w-full py-6 text-lg font-outfit font-semibold rounded-xl transition-all duration-300 ${
                addedToCart
                  ? "bg-green-600 hover:bg-green-600"
                  : "bg-primary text-primary-foreground hover:bg-primary/90 neon-box"
              }`}
            >
              {addedToCart ? (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Added to Cart
                </>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Cart - ${(product.price * quantity).toFixed(2)}
                </>
              )}
            </Button>

            {/* Pickup Info */}
            <div className="mt-6 p-4 rounded-xl bg-card border border-border">
              <p className="font-outfit text-sm text-muted-foreground">
                <strong className="text-foreground">Store Pickup Only</strong>
                <br />
                1823 E Santa Fe St, Gardner, KS 66030
                <br />
                Open Daily: 10 AM - 8 PM
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductPage;
